import React, { useState } from 'react';
import { useLicense } from '../contexts/LicenseContext';
import { isDesktopElectronBuild } from '../utils/licenseDesktopPolicy';
import './LicenseEntry.css';

const LicenseEntry = ({ onLicenseValidated }) => {
  const [licenseKey, setLicenseKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { validateLicense } = useLicense();

  const handleLicenseSubmit = async (e) => {
    e.preventDefault();
    
    if (!licenseKey.trim()) {
      setError('Please enter a license key');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('Validating license:', licenseKey);
      
      // Use the new license context
      const result = await validateLicense(licenseKey);
      
      if (result.isValid) {
        // Show success message
        setSuccess(`License validated successfully! System registered (0/0 systems)`);
        
        // Call the callback to notify parent component
        if (onLicenseValidated) {
          onLicenseValidated(result.license);
        }
        
        // Clear the form
        setLicenseKey('');
        
        console.log('License validation successful:', {
          licenseKey: licenseKey,
          result: result
        });
      } else {
        setError(result.reason || 'License validation failed');
      }
    } catch (error) {
      console.error('License validation error:', error);
      setError(error.message || 'Network error. Please check your internet connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyChange = (e) => {
    const value = e.target.value;
    setLicenseKey(value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  return (
    <div className="license-entry-container">
      <div className="license-entry-card">
        <div className="license-entry-header">
          <h1>🔐 License Verification</h1>
          <p>Please enter your license key to continue using the application</p>
          {isDesktopElectronBuild() && (
            <p className="license-entry-desktop-hint" style={{ marginTop: 12, fontSize: '0.95rem', lineHeight: 1.45, color: '#444' }}>
              This desktop app requires an <strong>Ultimate</strong> license or a qualifying <strong>Lifetime</strong> license.
              Keys sold for web and mobile only (Standard) will not activate here; use the website or Android app with those keys.
            </p>
          )}
        </div>

        <form onSubmit={handleLicenseSubmit} className="license-form">
          <div className="input-group">
            <label htmlFor="licenseKey">License Key</label>
            <input
              type="text"
              id="licenseKey"
              value={licenseKey}
              onChange={handleKeyChange}
              placeholder="Enter your license key (e.g., FD-XXXX-XXXX-XXXX)"
              className="license-input"
              disabled={isLoading}
              autoFocus
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              {success}
            </div>
          )}

          <button
            type="submit"
            className="validate-btn"
            disabled={isLoading || !licenseKey.trim()}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Validating...
              </>
            ) : (
              'Validate License'
            )}
          </button>
        </form>

        <div className="license-info">
          <h3>📋 License Information</h3>
          <ul>
            <li>🖥️ Registers your system automatically</li>
            {!isDesktopElectronBuild() && (
              <li>🌐 Web and Android use Standard or Ultimate; desktop (Electron) needs Ultimate or qualifying Lifetime</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LicenseEntry; 